/**
 * Batch Genealogy — module entry / orchestrator.
 *
 * This is an in-app full-screen view (not a routed page / no URL). It is opened
 * and closed by ProductDevelopment via props.
 *
 * Two entry points (per MOSYMPD-453):
 *   1. From Project Analytics (fromProjectAnalytics) → the selection-filters
 *      page (Project ID, Campaign ID, Stage-Batch Number) → View Report, with
 *      a "Return to Main Dashboard" link (closes both this view and Project
 *      Analytics, landing back on the dashboard).
 *   2. From the campaign / batch view → a `batchContext` prop is supplied and
 *      the report opens DIRECTLY for that batch, skipping the filters (and the
 *      "Return to Main Dashboard" link is hidden).
 *
 * The report page shows the ribbon summary, a Lineage graph / Lineage table
 * toggle, and raises the batch-detail and all-deviations side panels.
 *
 * All data is fetched through ./api (dummy now, middleware-only later).
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { STATUS } from './statusConfig';
import { fetchBatchGenealogyReport, exportLineageCSV, exportLineagePNG } from './api';
import SelectionFilters from './components/SelectionFilters';
import ReportRibbon from './components/ReportRibbon';
import LineageGraph from './components/LineageGraph';
import LineageTable from './components/LineageTable';
import BatchDetailDrawer from './components/BatchDetailDrawer';
import AllDeviationsDrawer from './components/AllDeviationsDrawer';
import { useProjectDevNav } from '../ProjectDevNavContext';

const useStyles = makeStyles((theme) => ({
  // fontFamily here cascades the app's font (MUI Roboto stack) to every bare
  // div/span in this module — they'd otherwise fall back to the sanitize.css
  // `system-ui` on <html> and look different from the rest of the app. Raw
  // <button> elements don't inherit font, so each sets fontFamily: 'inherit'.
  // NOTE: no `minHeight: 100%` here. Forcing full height combined with the
  // fixed-height graph made the whole report overflow the viewport, so both the
  // window AND the layout <main> (whose `overflowX:auto` makes overflowY compute
  // to auto too) showed a scrollbar — the "two scrolls". Without it, a report
  // taller than the viewport (the full-size Lineage graph) is scrolled by the
  // page alone.
  container: { padding: theme.spacing(3), paddingTop: theme.spacing(2), background: '#f4f6fa', fontFamily: theme.typography.fontFamily },
  // The Lineage table view area — a single viewport-bounded region the table
  // scrolls inside. (The Lineage graph is NOT bounded by it: it renders at design
  // size and grows to the lineage's height, scrolled by the page itself.)
  viewRegion: {
    minHeight: 340,
    [theme.breakpoints.up('xl')]: { height: 'calc(100vh - 360px)' },
    [theme.breakpoints.down('xl')]: { height: 'calc(100vh - 320px)' },
    [theme.breakpoints.down('md')]: { height: 'calc(100vh - 260px)' },
  },
  returnLink: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#4338ca', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginBottom: 16, '&:hover': { textDecoration: 'underline' } },
  crumbRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 14 },
  back: { display: 'inline-flex', alignItems: 'center', color: '#000000', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },
  crumbSep: { color: '#000000' },
  crumbCurrent: { color: '#000000' },
  toggleWrap: { display: 'inline-flex', border: '1px solid #d7dbe0', borderRadius: 8, overflow: 'hidden', marginBottom: 14 },
  toggleBtn: { border: 'none', background: '#fff', padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#000000', cursor: 'pointer', fontFamily: 'inherit' },
  toggleOn: { background: '#eef2ff', color: '#4338ca' },
  legend: { display: 'flex', gap: 18, flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#000000' },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  toolbar: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  loading: { padding: 60, textAlign: 'center', color: '#333333', fontSize: 15 },
}));

const BatchGenealogy = ({
  batchContext = null,
  fromProjectAnalytics = false,
  onReturnToMainDashboard,
  onClose,
}) => {
  const classes = useStyles();
  const { openProjectTab } = useProjectDevNav();

  // Arrived DIRECTLY from the dashboard/batch view (Entry point 2) when a
  // batchContext is supplied. In that case we skip the selection filters and
  // hide the "Return to Main Dashboard" link, since the user did not come
  // from Project Analytics.
  const cameDirect = Boolean(batchContext);

  // On a direct entry we open straight into the report (loading) so the
  // selection-filters page never flashes before the report loads.
  const [mode, setMode] = useState(cameDirect ? 'report' : 'select'); // 'select' | 'report'
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(cameDirect);
  const [view, setView] = useState('graph'); // 'graph' | 'table'
  const [selectedBatch, setSelectedBatch] = useState(null); // batch detail drawer
  const [showAllDeviations, setShowAllDeviations] = useState(false);
  // Selection filters live here (not inside SelectionFilters) so the user's
  // chosen Project / Campaign / Batch are preserved when they open a report and
  // then come Back to the filters page.
  const [filters, setFilters] = useState({ projectId: '', campaignId: '', batchNumber: '' });
  // True when the batch-detail drawer was opened FROM the "Batches with
  // deviations & failures" panel — closing the detail then returns to that panel
  // instead of leaving the user on the report.
  const [cameFromDeviations, setCameFromDeviations] = useState(false);

  // Open the batch-detail drawer. `fromDeviations` records where we came from so
  // closing the drawer can restore the deviations panel (see BatchDetailDrawer).
  const openBatchDetail = useCallback((batchId, fromDeviations = false) => {
    setCameFromDeviations(fromDeviations);
    setSelectedBatch(batchId);
  }, []);

  const closeBatchDetail = useCallback(() => {
    setSelectedBatch(null);
    if (cameFromDeviations) {
      setCameFromDeviations(false);
      setShowAllDeviations(true); // reopen the deviations panel we came from
    }
  }, [cameFromDeviations]);

  // Navigate to the batch's Deviations tab. Closes this full-screen report and
  // asks the dashboard to point its Campaign / CMO Stage / CMO Batch selection
  // at the batch shown in the detail drawer, then open the "Deviations" tab for
  // it. `selectedBatch` is that batch's number (the lineage node id); the report
  // header carries the campaign. The deviation object is unused for now — the
  // tab lists all of the batch's deviations.
  const goToDeviationTab = useCallback(() => {
    if (!openProjectTab) return;
    openProjectTab('Deviations', {
      batchNumber: selectedBatch,
      campaignId: report?.header?.campaign,
    });
  }, [openProjectTab, selectedBatch, report]);

  // guard against setting state after unmount (report fetch is async)
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Export to QA — download BOTH the Lineage chart (PNG) and the Lineage table
  // (CSV) from a single click, with no zip. The chart is rendered deterministically
  // from the report data (independent of the current graph/table view), and the CSV
  // always downloads even if the chart render fails (fallback = table only).
  // The table is CSV to match the button label; exportLineageXLS remains available
  // in api.js if a styled workbook is wanted again.
  const handleExportToQA = useCallback(async () => {
    if (!report) return;
    try {
      await exportLineagePNG(report.header, report.nodes, report.steps);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BatchGenealogy] chart PNG export failed; downloading table only:', err);
    }
    try {
      exportLineageCSV(report.header, report.nodes, report.steps);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BatchGenealogy] table CSV export failed:', err);
    }
  }, [report]);

  const loadReport = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await fetchBatchGenealogyReport(params);
      if (!mountedRef.current) return;
      if (res.status && res.data) {
        setReport(res.data);
        setMode('report');
        setView('graph');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading batch genealogy report:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Entry point 2 — direct batch from the campaign / batch view. Opening from
  // the batch view always carries a batchContext; open the report directly for
  // any context passed (the API resolves a lineage even if the exact batch
  // isn't in the dummy dataset yet), so it never bounces to the filters.
  useEffect(() => {
    const ctx = batchContext;
    if (ctx && (ctx.batchNumber || ctx.projectId || ctx.campaignId)) loadReport(ctx);
    else if (cameDirect) setLoading(false); // no usable ids — don't hang on the spinner
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arrived here from Project Analytics — the "Return to Main Dashboard" link is
  // shown only in that case, and clicking it closes BOTH Project Analytics and
  // Batch Genealogy, dropping the user back on the main dashboard.
  const cameFromProjectAnalytics = Boolean(fromProjectAnalytics);

  const goToMainDashboard = () => { if (onReturnToMainDashboard) onReturnToMainDashboard(); };

  const handleBackFromReport = () => {
    // If we arrived directly (no filters) from the batch view, Back closes the
    // whole view (returns to wherever it was opened from); otherwise it returns
    // to the selection page.
    if (cameDirect) { if (onClose) onClose(); }
    else { setMode('select'); setReport(null); }
  };

  const lineageIds = useMemo(() => (report ? report.nodes.map((n) => n.id) : []), [report]);

  // Step entry ({ id, label, desc }) of the batch open in the detail drawer — its
  // product description is shown there as the "Stage X Intermediate" field.
  const selectedStep = useMemo(() => {
    if (!report || !selectedBatch) return null;
    const node = report.nodes.find((n) => n.id === selectedBatch);
    return node ? (report.steps || []).find((s) => s.id === node.step) || null : null;
  }, [report, selectedBatch]);

  return (
    <div className={classes.container}>
      {cameFromProjectAnalytics && (
        <span className={classes.returnLink} onClick={goToMainDashboard}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Return to Main Dashboard
        </span>
      )}

      {mode === 'select' && (
        <SelectionFilters
          filters={filters}
          setFilters={setFilters}
          onViewReport={loadReport}
          loading={loading}
        />
      )}

      {mode === 'report' && (
        <>
          <div className={classes.crumbRow}>
            <span className={classes.back} onClick={handleBackFromReport}>
              <ChevronLeftIcon fontSize="small" /> Back
            </span>
            <span className={classes.crumbSep}>/</span>
            <span className={classes.crumbCurrent}>Genealogy Report</span>
          </div>

          {loading || !report ? (
            <div className={classes.loading}>Loading genealogy report…</div>
          ) : (
            <>
              <ReportRibbon
                header={report.header}
                onAllDeviations={() => setShowAllDeviations(true)}
                onExport={handleExportToQA}
              />

              <div className={classes.toolbar}>
                <div className={classes.toggleWrap}>
                  <button
                    type="button"
                    className={`${classes.toggleBtn} ${view === 'graph' ? classes.toggleOn : ''}`}
                    onClick={() => setView('graph')}
                  >
                    ▤ Lineage graph
                  </button>
                  <button
                    type="button"
                    className={`${classes.toggleBtn} ${view === 'table' ? classes.toggleOn : ''}`}
                    onClick={() => setView('table')}
                  >
                    ☰ Lineage table
                  </button>
                </div>
                <div className={classes.legend}>
                  {Object.values(STATUS).map((s) => (
                    <span key={s.key} className={classes.legendItem}>
                      <span className={classes.dot} style={{ background: s.color }} />{s.label}
                    </span>
                  ))}
                </div>
              </div>

              {view === 'graph' ? (
                // Sizes itself to the full lineage at full size (no fitting into
                // the viewport), so the page scroll reveals the lower stages.
                <LineageGraph
                  steps={report.steps}
                  nodes={report.nodes}
                  onSelectBatch={(id) => openBatchDetail(id, false)}
                />
              ) : (
                <div className={classes.viewRegion}>
                  <LineageTable
                    nodes={report.nodes}
                    steps={report.steps}
                    onSelectBatch={(id) => openBatchDetail(id, false)}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {selectedBatch && (
        <BatchDetailDrawer
          batchId={selectedBatch}
          step={selectedStep}
          onClose={closeBatchDetail}
          onOpenDeviation={goToDeviationTab}
        />
      )}

      {showAllDeviations && report && (
        <AllDeviationsDrawer
          campaignId={report.header.campaign}
          lineageBatchIds={lineageIds}
          onClose={() => setShowAllDeviations(false)}
          onViewBatch={(batchId) => { setShowAllDeviations(false); openBatchDetail(batchId, true); }}
          onOpenQMS={() => { /* opens the corresponding QMS deviation record */ }}
        />
      )}
    </div>
  );
};

export default BatchGenealogy;
